/**
 * WebinarPageAnimator
 * Gère les animations scroll-triggered et la classification passé/à venir.
 */
export class WebinarPageAnimator {
  private stage: HTMLElement;
  private sections: HTMLElement[];
  private calendarCards: HTMLElement[];
  private sectionObserver: IntersectionObserver | null = null;
  private calendarObserver: IntersectionObserver | null = null;
  private reducedMotion: boolean;

  constructor(container: HTMLElement) {
    this.stage = container;
    this.sections = Array.from(container.querySelectorAll<HTMLElement>('.webinar-section'));
    this.calendarCards = Array.from(document.querySelectorAll<HTMLElement>('.webinar-calendar__card'));
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  public initialize(): void {
    this.classifyWebinars();

    if (this.reducedMotion) {
      this.showAllImmediately();
      return;
    }

    this.bindSectionObserver();
    this.bindCalendarObserver();
    this.bindSmoothScroll();
  }

  public destroy(): void {
    this.sectionObserver?.disconnect();
    this.calendarObserver?.disconnect();
  }

  /**
   * Classe chaque webinaire comme passé (.is-past) ou à venir.
   * Marque le prochain webinaire futur (.is-next / .is-featured).
   * Si tous sont passés : aucun badge "prochain".
   *
   * Aperçu : ?preview=empty force l'état "aucun webinaire à venir" (pour visualiser
   * le bandeau d'attente en local ou en prod sans attendre que les dates passent).
   */
  private classifyWebinars(): void {
    const forceEmpty = new URLSearchParams(window.location.search).get('preview') === 'empty';
    const now = new Date();
    let nextIndex = -1;
    let nearestDiff = Infinity;

    this.sections.forEach((section, i) => {
      const dateStr = section.getAttribute('data-date');
      if (!dateStr) return;

      const eventDate = new Date(dateStr);
      const diff = eventDate.getTime() - now.getTime();

      if (forceEmpty || diff <= 0) {
        section.classList.add('is-past');
        if (this.calendarCards[i]) this.calendarCards[i].classList.add('is-past');
      } else if (diff < nearestDiff) {
        nearestDiff = diff;
        nextIndex = i;
      }
    });

    if (!forceEmpty && nextIndex >= 0) {
      this.sections[nextIndex].classList.add('is-featured');
      if (this.calendarCards[nextIndex]) this.calendarCards[nextIndex].classList.add('is-next');
    } else if (this.sections.length > 0) {
      // Aucun webinaire à venir : on bascule la page en mode "archive".
      this.enterEmptyState();
    }
  }

  /**
   * Active l'état "aucun webinaire à venir" :
   *  - révèle le bandeau d'attente (#webinar-empty) avec capture d'email,
   *  - révèle l'en-tête "Éditions passées" au-dessus de la timeline,
   *  - requalifie le CTA global en invitation à être prévenu de la reprise.
   * Les CTA d'inscription des sections passées sont masqués via CSS (.is-all-past).
   */
  private enterEmptyState(): void {
    this.stage.classList.add('is-all-past');

    const empty = document.getElementById('webinar-empty');
    if (empty) {
      empty.hidden = false;
      empty.removeAttribute('aria-hidden');
    }

    const archiveHead = document.getElementById('webinar-archive-head');
    if (archiveHead) archiveHead.hidden = false;

    const globalCta = document.querySelector<HTMLAnchorElement>('.webinar-global-cta__btn');
    if (globalCta) {
      globalCta.setAttribute('href', '#webinar-empty');
      // On retire data-translate pour que le moteur i18n ne réécrase pas ce libellé.
      globalCta.removeAttribute('data-translate');
      globalCta.textContent = 'Être prévenu de la reprise';
    }
  }

  private bindSectionObserver(): void {
    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = entry.target as HTMLElement;
            section.classList.add('is-visible');

            const topicCards = section.querySelectorAll<HTMLElement>('.webinar-topic-card, .webinar-featured-card');
            topicCards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('is-visible');
              }, 150 + i * 100);
            });

            this.sectionObserver?.unobserve(section);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    this.sections.forEach((section) => {
      this.sectionObserver!.observe(section);
    });
  }

  private bindCalendarObserver(): void {
    if (this.calendarCards.length === 0) return;

    const calendarSection = document.querySelector('.webinar-calendar');
    if (!calendarSection) return;

    this.calendarObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.calendarCards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add('is-visible');
              }, i * 150);
            });
            this.calendarObserver?.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );

    this.calendarObserver.observe(calendarSection);
  }

  private bindSmoothScroll(): void {
    this.calendarCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        const href = card.getAttribute('href');
        if (!href || !href.startsWith('#')) return;

        e.preventDefault();
        const target = document.querySelector(href);
        if (!target) return;

        const navbarHeight = 64;
        const top = target.getBoundingClientRect().top + window.scrollY - navbarHeight;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  private showAllImmediately(): void {
    this.calendarCards.forEach((card) => card.classList.add('is-visible'));
    this.sections.forEach((section) => {
      section.classList.add('is-visible');
      section.querySelectorAll<HTMLElement>('.webinar-topic-card, .webinar-featured-card').forEach((card) => {
        card.classList.add('is-visible');
      });
    });
    this.bindSmoothScroll();
  }
}

export function initWebinarPage(): void {
  const stage = document.querySelector<HTMLElement>('.webinar-stage');
  if (!stage) return;

  const animator = new WebinarPageAnimator(stage);
  animator.initialize();
}
